/**
 * The bell's chime.
 *
 * Synthesised rather than played from a file, and that is the considered choice: a two-tone
 * chime is about thirty lines of oscillator, which is smaller than any `.mp3` worth
 * shipping, needs no second network request on a workshop's tethered 4G, and works the
 * first time in an offline tab. There is also nothing to get 404 behind a CDN rule.
 *
 * ── The autoplay gate ─────────────────────────────────────────────────────────
 *
 * Browsers refuse to start an `AudioContext` until the page has seen a real user gesture,
 * and a refused one stays `suspended` forever rather than throwing. So the context is
 * created lazily and resumed from a one-shot listener on the first click or keypress
 * anywhere in the app. A tablet that is left open at the counter all morning has, by then,
 * been touched — and "Testa ljudet" on the settings screen is itself a gesture, which is
 * why that button doubles as the fix when someone reports that the sound does not work.
 */

let context: AudioContext | null = null;

/** DESIGN.md's quiet end of the register: audible across a bay, not startling at a desk. */
interface Tone {
  readonly hz: number;
  readonly at: number;
  readonly seconds: number;
}

const TONES: readonly Tone[] = [
  { hz: 880, at: 0, seconds: 0.18 },
  { hz: 1174.66, at: 0.13, seconds: 0.26 },
];

const ORDER_TONES: readonly Tone[] = [
  { hz: 659.25, at: 0, seconds: 0.16 },
  { hz: 783.99, at: 0.11, seconds: 0.16 },
  { hz: 1046.5, at: 0.22, seconds: 0.34 },
];

const PEAK_GAIN = 0.12;

function audioContext(): AudioContext | null {
  if (context !== null) return context;

  // Safari still needs the prefix, and a browser with neither is one where the bell is
  // silent and everything else works.
  const Ctor =
    window.AudioContext ??
    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (Ctor === undefined) return null;

  context = new Ctor();
  return context;
}

/**
 * Let the first gesture anywhere in the app unlock audio.
 *
 * Mounted once from the bell. `once: true` on both listeners, because after the first
 * resume there is nothing left to do and a listener on every click for the life of the tab
 * is a cost with no return.
 */
export function armSound(): () => void {
  const resume = () => {
    const ctx = audioContext();
    if (ctx !== null && ctx.state === "suspended") void ctx.resume();
  };

  window.addEventListener("pointerdown", resume, { once: true });
  window.addEventListener("keydown", resume, { once: true });

  return () => {
    window.removeEventListener("pointerdown", resume);
    window.removeEventListener("keydown", resume);
  };
}

/**
 * Play the chime. Silent, never throwing, when audio is unavailable or still locked.
 *
 * A notification that crashes the bell it is announcing would be worse than one nobody
 * hears, so every failure here is swallowed: a blocked context, a browser with no Web
 * Audio, an OS that has muted the tab.
 *
 * Resolves `true` if the chime actually started. The caller is not obliged to care — the
 * bell does not — but the settings screen's "Testa ljudet" does, because a test button that
 * silently does nothing is indistinguishable from a broken feature and is the exact moment
 * someone needs to be told their browser is holding the audio back.
 *
 * The resume is **awaited**. It used to return early and leave the chime for next time,
 * which meant the first notification after a page load was always the silent one: a tab that
 * has been sitting on the calendar untouched has a suspended context, and "untouched" is the
 * normal state of a workshop's wall tablet between bookings.
 */
export function playChime(): Promise<boolean> {
  return playTones(TONES, "sine");
}

export function playOrderChime(): Promise<boolean> {
  return playTones(ORDER_TONES, "triangle");
}

async function playTones(tones: readonly Tone[], type: OscillatorType): Promise<boolean> {
  const ctx = audioContext();
  if (ctx === null) return false;

  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch {
      // Refused for want of a gesture. Nothing to do but stay quiet.
      return false;
    }
  }

  // Still suspended after a resume that did not throw: Chrome resolves the promise and
  // leaves the state alone when there has been no gesture.
  if (ctx.state !== "running") return false;

  const now = ctx.currentTime;

  for (const tone of tones) {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.value = tone.hz;

    // An exponential decay from a near-zero floor rather than a linear one: a square-edged
    // gain change clicks, and `exponentialRampToValueAtTime` refuses a target of exactly 0.
    const start = now + tone.at;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + tone.seconds);

    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(start + tone.seconds + 0.02);
  }

  return true;
}
